import { createContext, useContext, ReactNode } from "react";
import type { DBProject } from "@/lib/db-types";
import type { BudgetLineRow, CustomerInfo, ProjectOwnerWithUser, TimeEntry, VoucherAllocationRow } from "@/hooks/use-project-detail";
import type { UserLookup } from "@/hooks/use-users";
import type { useProjectMutations } from "@/hooks/use-project-mutations";
import type { ProjectPermissionKey } from "@/hooks/use-project-permission";

/**
 * Shared state and data for the project detail view.
 * Wraps all tab routes so they don't have to prop-drill.
 */
export interface ProjectDetailContextValue {
  // Core data
  project: DBProject;
  customer: CustomerInfo | null;
  owners: ProjectOwnerWithUser[];
  invoices: any[];
  vouchers: any[];
  voucherAllocations: VoucherAllocationRow[];
  timeEntries: TimeEntry[];
  budgetLines: BudgetLineRow[];
  offerMetadata: any;
  allUsers: UserLookup[];
  userMap: Map<string, UserLookup>;

  // Permissions & roles
  isAdmin: boolean;
  isProductionLead: boolean;
  isProjectOwner: boolean;
  canSeeBudget: boolean;
  canAssignHours: boolean;
  internalReadOnly: boolean;
  effectiveProductionLeadId: string | null;
  activeUserId: string | null;

  /**
   * VIB-722b: ferdigberegnet permission-map for alle 17 prosjekt-keys, med
   * 3-nivå-resolution (system-rolle → prosjekt-rolle → assignment).
   * Hentes fra `useProjectPermissions(projectId)`.
   *
   * VIB-722c kobler eksisterende permission-sjekker (canSeeBudget,
   * isProductionLead, isAdmin) om til å lese fra dette mappet.
   */
  permissions: Record<ProjectPermissionKey, boolean>;

  // Mutations
  mutations: ReturnType<typeof useProjectMutations>;
}

const ProjectDetailContext = createContext<ProjectDetailContextValue | null>(null);

export function ProjectDetailProvider({
  value,
  children,
}: {
  value: ProjectDetailContextValue;
  children: ReactNode;
}) {
  return (
    <ProjectDetailContext.Provider value={value}>
      {children}
    </ProjectDetailContext.Provider>
  );
}

export function useProjectDetailContext(): ProjectDetailContextValue {
  const ctx = useContext(ProjectDetailContext);
  if (!ctx) {
    throw new Error("useProjectDetailContext must be used within ProjectDetailProvider");
  }
  return ctx;
}
